import { DOCUMENT, Injectable, inject } from '@angular/core';
import { AbstractSecurityStorage } from 'angular-auth-oidc-client';

import { ConfigurationService } from '@mobi/rwc-utils-common-ng-jslib';

import { CIP_CONFIG_ID } from './cip-auth.config';

/**
 * Properties that must be stored in session storage, because angular-auth-oidc-client needs
 * them to persist the flicker-flacker™-journey.
 *
 * They are short-lived and technical and do not contain sensitive data.
 *
 * All other properties do potentially contain sensitive data and are therefore
 * only stored in memory.
 */
const SESSION_ALLOWED_KEYS = new Set([
    'redirect',
    'storageCustomParamsAuthRequest',
    'storageCodeFlowInProgress',
    'authWellKnownEndPoints',
    'issuer',
    'storageSilentRenewRunning',
    'authStateControl',
    'authNonce',
    'codeVerifier'
]);

declare global {
    interface Window {
        inMemoryStorage: Map<string, Record<string, unknown>>;
    }
}

@Injectable({
    providedIn: 'root'
})
export class CipSecurityStorageService implements AbstractSecurityStorage {
    #sessionStorage = inject(DOCUMENT).defaultView?.sessionStorage;
    #inMemoryStorage = new Map<string, Record<string, unknown>>();
    #configurationService = inject(ConfigurationService);

    constructor() {
        if (!this.#configurationService.isProduction()) {
            window['inMemoryStorage'] = this.#inMemoryStorage;
        }
    }

    read(key: string): string | null {
        const sessionStorageData = this.#sessionStorage?.getItem(key);
        const inMemoryData = this.#inMemoryStorage.get(key);

        if (!sessionStorageData && !inMemoryData) {
            return null;
        }

        return JSON.stringify({
            ...(sessionStorageData ? JSON.parse(sessionStorageData) : {}),
            ...(inMemoryData ?? {})
        });
    }

    write(key: string, value: string): void {
        const sessionStorageData: Record<string, unknown> = {};
        const inMemoryData: Record<string, unknown> = {};

        for (const [name, entryValue] of Object.entries(JSON.parse(value) as Record<string, unknown>)) {
            (key !== CIP_CONFIG_ID || SESSION_ALLOWED_KEYS.has(name) ? sessionStorageData : inMemoryData)[name] =
                entryValue;
        }

        this.#sessionStorage?.setItem(key, JSON.stringify(sessionStorageData));
        this.#inMemoryStorage.set(key, inMemoryData);
    }

    remove(key: string): void {
        this.#sessionStorage?.removeItem(key);
        this.#inMemoryStorage.delete(key);
    }

    clear(): void {
        this.#sessionStorage?.clear();
        this.#inMemoryStorage.clear();
    }
}
